import { type ComponentType, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@sonify/web-ui";

export interface GroupedDropdownItem {
  key: string;
  label: string;
  icon?: ComponentType<{ size?: number }>;
}

export interface GroupedDropdownProps {
  label: string;
  icon?: ComponentType<{ size?: number }>;
  items: readonly GroupedDropdownItem[];
  activeKey: string | null;
  onSelect: (key: string) => void;
  renderItem?: (item: GroupedDropdownItem, isActive: boolean) => ReactNode;
}

export function GroupedDropdown({
  label,
  icon: Icon,
  items,
  activeKey,
  onSelect,
  renderItem,
}: GroupedDropdownProps) {
  const activeItem = items.find((i) => i.key === activeKey);
  const isGroupActive = activeItem != null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1.5 px-3 text-sm font-medium transition-colors border-b-2 -mb-px outline-none",
            isGroupActive
              ? "border-foreground-accent text-foreground"
              : "border-transparent text-foreground-muted hover:text-foreground hover:border-foreground-muted/30",
          )}
        >
          {Icon && <Icon size={14} />}
          <span>{label}</span>
          {activeItem && (
            <>
              <span className="text-foreground-muted">:</span>
              <span>{activeItem.label}</span>
            </>
          )}
          <ChevronDown size={12} className="opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8}>
        {items.map((item) => {
          const isActive = item.key === activeKey;

          if (renderItem) {
            return (
              <DropdownMenuItem key={item.key} asChild>
                {renderItem(item, isActive)}
              </DropdownMenuItem>
            );
          }

          const ItemIcon = item.icon;
          return (
            <DropdownMenuItem
              key={item.key}
              onSelect={() => onSelect(item.key)}
              className={cn(
                "flex items-center gap-2 cursor-pointer",
                isActive && "text-foreground-accent",
              )}
            >
              {ItemIcon && <ItemIcon size={14} />}
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
